import React, { useState, useEffect, useCallback, useRef } from 'react';
import { listHuscleosTestBatches, runHuscleosTestBatch, deleteHuscleosTestBatch, huscleosDemoLogin } from '../../lib/api';
import {
  Beaker, Plus, X, Loader2, Heart, Dumbbell, Flame, LogIn, Trash2, Copy, CheckCircle2, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const TYPE_META = {
  boutique:  { icon: <Heart size={14} />,    label: 'Boutique',  color: '#C2847A' },
  strength:  { icon: <Dumbbell size={14} />, label: 'Strength',  color: '#0D5D3E' },
  endurance: { icon: <Flame size={14} />,    label: 'Endurance', color: '#6366F1' },
};

export default function DemoSwitcher() {
  const [open, setOpen] = useState(false);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [firing, setFiring] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newGymType, setNewGymType] = useState('strength');
  const [newGymName, setNewGymName] = useState('');
  const ref = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listHuscleosTestBatches();
      setBatches(data.batches || []);
    } catch (err) {
      // Silent: demo switcher is a dev convenience
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const fireBatch = async () => {
    setFiring(true);
    try {
      const nextNum = batches.length + 1;
      await runHuscleosTestBatch({
        batch_num: nextNum,
        gym_type: newGymType,
        email: 'management@huscleos.com',
        gym_name: newGymName || `Demo Batch ${nextNum} — ${TYPE_META[newGymType].label} Gym`,
        contact_name: `Batch ${nextNum} Owner`,
      });
      toast.success(`Batch ${nextNum} fired — all 6 emails on their way.`);
      setCreating(false);
      setNewGymName('');
      await load();
    } catch (err) {
      toast.error('Failed to fire batch');
    } finally {
      setFiring(false);
    }
  };

  const openDemo = async (leadId) => {
    try {
      const { data } = await huscleosDemoLogin(leadId);
      // Stash the token under the existing auth key, then open the staff dashboard in a new tab
      window.open(`/staff/dashboard?demo_token=${encodeURIComponent(data.access_token)}`, '_blank');
      toast.success(`Opened demo: ${data.demo_gym}`);
    } catch (err) {
      toast.error('Failed to open demo');
    }
  };

  const copyCreds = (b) => {
    const text = `URL: ${window.location.origin}/staff/login\nEmail: ${b.demo_email}\nPassword: ${b.demo_password}`;
    navigator.clipboard.writeText(text);
    toast.success('Demo credentials copied');
  };

  const deleteBatch = async (leadId, gymName) => {
    if (!window.confirm(`Delete demo batch "${gymName}"? This removes the demo user, 20 sandbox leads, events, and blog posts.`)) return;
    try {
      await deleteHuscleosTestBatch(leadId);
      toast.success('Batch deleted');
      await load();
    } catch (err) {
      toast.error('Failed to delete batch');
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="demo-switcher-trigger"
        className="flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase px-4 py-1.5 transition-colors"
        style={{ background: '#FF2E00', color: '#fff' }}
      >
        <Beaker size={14} /> Demos {batches.length > 0 && <span className="ml-1 text-[10px] bg-white/25 px-1.5 py-0.5 rounded">{batches.length}</span>}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[420px] bg-white border border-gray-200 shadow-2xl z-50 max-h-[70vh] overflow-y-auto"
          data-testid="demo-switcher-panel"
        >
          <div className="sticky top-0 flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
            <div className="flex items-center gap-2">
              <Beaker size={14} className="text-[#FF2E00]" />
              <span className="text-xs font-bold tracking-widest uppercase" style={{ fontFamily: 'Manrope', color: '#0F0F0F' }}>Sandbox Demos</span>
            </div>
            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-900"><X size={14} /></button>
          </div>

          <div className="p-3 border-b border-gray-200">
            {!creating ? (
              <button
                onClick={() => setCreating(true)}
                data-testid="demo-switcher-new-batch"
                className="w-full flex items-center justify-center gap-2 py-2.5 text-xs font-bold tracking-widest uppercase text-gray-700 hover:bg-gray-50 border border-dashed border-gray-300 transition-colors"
              >
                <Plus size={14} /> Fire New Demo Batch
              </button>
            ) : (
              <div className="space-y-2">
                <div>
                  <label className="block text-[10px] font-bold tracking-widest uppercase text-gray-500 mb-1">Gym Type</label>
                  <div className="grid grid-cols-3 gap-1.5">
                    {Object.entries(TYPE_META).map(([key, meta]) => (
                      <button
                        key={key}
                        onClick={() => setNewGymType(key)}
                        data-testid={`demo-switcher-type-${key}`}
                        className={`flex items-center justify-center gap-1 py-2 text-[10px] font-bold tracking-wider uppercase transition-colors ${newGymType === key ? 'text-white' : 'text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200'}`}
                        style={{ background: newGymType === key ? meta.color : undefined }}
                      >
                        {meta.icon} {meta.label}
                      </button>
                    ))}
                  </div>
                </div>
                <input
                  type="text"
                  value={newGymName}
                  onChange={(e) => setNewGymName(e.target.value)}
                  placeholder={`Demo Batch ${batches.length + 1} — ${TYPE_META[newGymType].label} Gym`}
                  data-testid="demo-switcher-gym-name"
                  className="w-full px-3 py-2 text-xs bg-gray-50 border border-gray-200 focus:outline-none focus:border-gray-900 transition-colors"
                  style={{ color: '#0F0F0F' }}
                />
                <div className="flex gap-2">
                  <button
                    onClick={fireBatch}
                    disabled={firing}
                    data-testid="demo-switcher-fire"
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-bold tracking-widest uppercase text-white bg-[#FF2E00] hover:opacity-90 transition-all disabled:opacity-50"
                  >
                    {firing ? <><Loader2 size={12} className="animate-spin" /> Firing...</> : <><Zap size={12} /> Fire Batch</>}
                  </button>
                  <button onClick={() => setCreating(false)} className="px-3 py-2 text-xs text-gray-500 hover:text-gray-900">Cancel</button>
                </div>
                <p className="text-[10px] text-gray-500 leading-relaxed mt-2">
                  Spins up a sandboxed demo + fires all 6 drip emails to <span className="font-mono text-gray-900">management@huscleos.com</span> in ~20 seconds. Demo is fully isolated — nothing it does can affect real data.
                </p>
              </div>
            )}
          </div>

          <div className="divide-y divide-gray-100">
            {loading && batches.length === 0 && (
              <div className="flex items-center justify-center py-10"><Loader2 size={18} className="animate-spin text-gray-400" /></div>
            )}
            {!loading && batches.length === 0 && (
              <div className="py-10 px-4 text-center">
                <p className="text-xs text-gray-400">No test batches yet. Fire one above.</p>
              </div>
            )}
            {batches.map(b => {
              const meta = TYPE_META[b.gym_type] || TYPE_META.strength;
              return (
                <div key={b.lead_id} className="p-3 hover:bg-gray-50 transition-colors" data-testid={`demo-switcher-batch-${b.batch_num}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <div style={{ color: meta.color }}>{meta.icon}</div>
                        <span className="text-[10px] font-bold tracking-widest uppercase" style={{ color: meta.color }}>
                          Batch {b.batch_num} · {meta.label}
                        </span>
                        {b.drip_step === 6 && <CheckCircle2 size={11} className="text-green-600" />}
                      </div>
                      <p className="text-sm font-bold truncate" style={{ fontFamily: 'Manrope', color: '#0F0F0F' }}>{b.gym_name}</p>
                      <p className="text-[10px] font-mono text-gray-400 truncate">{b.demo_email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => openDemo(b.lead_id)}
                      data-testid={`demo-switcher-open-${b.batch_num}`}
                      className="flex-1 flex items-center justify-center gap-1 py-1.5 text-[10px] font-bold tracking-widest uppercase text-white bg-gray-900 hover:bg-black transition-colors"
                    >
                      <LogIn size={11} /> Open
                    </button>
                    <button
                      onClick={() => copyCreds(b)}
                      title="Copy credentials"
                      className="p-1.5 text-gray-500 hover:text-gray-900 border border-gray-200 hover:border-gray-900 transition-colors"
                    >
                      <Copy size={11} />
                    </button>
                    <button
                      onClick={() => deleteBatch(b.lead_id, b.gym_name)}
                      title="Delete batch"
                      data-testid={`demo-switcher-delete-${b.batch_num}`}
                      className="p-1.5 text-gray-500 hover:text-red-500 border border-gray-200 hover:border-red-300 transition-colors"
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
